import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  Popconfirm,
  Radio,
  Select,
  Table,
  Tag,
  message,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { conferirPagamentosPix, getPagamentosPix } from "@/services/pix";
import { getUnidades } from "@/services/unidade";
import { PixType } from "@/types/PixType";
import { usePermissions } from "@/hooks/usePermissions";
import { errorHandler } from "@/utils/errorhandler";
import ComprovantesModal from "./Modals/comprovantesModal";

dayjs.locale("pt-br");

const { RangePicker } = DatePicker;

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

export default function ConsultaPix() {
  const { canRecebimentoFinanceiro } = usePermissions();
  const [pagamentos, setPagamentos] = useState<PixType[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadeIds, setUnidadeIds] = useState<number[]>([]);
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busca, setBusca] = useState("");
  const [filtroSituacao, setFiltroSituacao] =
    useState<"todos" | "aConferir" | "conferidos">("aConferir");
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [comprovantesPagamentoId, setComprovantesPagamentoId] = useState<number | null>(null);

  const carregarPagamentos = useCallback(async () => {
    try {
      const filtro = {
        conferido: filtroSituacao === "todos" ? undefined : filtroSituacao === "conferidos",
        unidadeIds: unidadeIds.length > 0 ? unidadeIds : undefined,
        dataInicio: periodo?.[0] ? periodo[0].format("YYYY-MM-DD") : undefined,
        dataFim: periodo?.[1] ? periodo[1].format("YYYY-MM-DD") : undefined,
        busca: busca.trim() || undefined,
      };
      const { data } = await getPagamentosPix(filtro);
      setPagamentos(data ?? []);
      setSelecionados([]);
    } catch (error) {
      errorHandler(error);
    }
  }, [filtroSituacao, unidadeIds, periodo, busca]);

  useEffect(() => {
    carregarPagamentos();
  }, [carregarPagamentos]);

  useEffect(() => {
    getUnidades()
      .then(({ data }) => setUnidades(data ?? []))
      .catch((error) => errorHandler(error));
  }, []);

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    try {
      await acao();
      message.success(sucesso);
      carregarPagamentos();
    } catch (error) {
      errorHandler(error);
    }
  };

  const totalFiltrado = useMemo(
    () => pagamentos.reduce((total, p) => total + (p.valor ?? 0), 0),
    [pagamentos]
  );

  // O modal lê da lista recarregada, então o anexo aparece sem fechar/reabrir.
  const pagamentoDosComprovantes = useMemo(
    () => pagamentos.find((p) => p.id === comprovantesPagamentoId) ?? null,
    [pagamentos, comprovantesPagamentoId]
  );

  const pendentesSelecionados = useMemo(
    () =>
      pagamentos
        .filter((p) => selecionados.includes(p.id) && !p.conferido && !p.trocado)
        .map((p) => p.id),
    [pagamentos, selecionados]
  );

  const colunas: ColumnsType<PixType> = [
    {
      title: "Status",
      key: "status",
      render: (_, registro) => {
        // PIX é à vista e não pode mais ser trocado. A tag continua aqui só para
        // registro criado enquanto a troca existiu: aparece, mas sem ação.
        if (registro.trocado)
          return <Tag color="purple">Trocado — {registro.trocaPagamentoNome}</Tag>;
        return registro.conferido ? (
          <Tag color="green">Conferido</Tag>
        ) : (
          <Tag color="default">A Conferir</Tag>
        );
      },
    },
    { title: "ID Aluno", dataIndex: "alunoId", key: "alunoId" },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    {
      title: "Responsável Financeiro",
      dataIndex: "nomeResponsavelFinanceiro",
      key: "nomeResponsavelFinanceiro",
    },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    {
      title: "Forma",
      key: "formaPagamento",
      render: (_, registro) => (
        <>
          <div>{registro.formaPagamento}</div>
          {registro.origemTrocaDescricao && (
            <Tag color="purple" className="mt-1">
              {registro.origemTrocaDescricao}
            </Tag>
          )}
        </>
      ),
    },
    { title: "Parcela", dataIndex: "parcela", key: "parcela" },
    {
      title: "Data Pagamento",
      dataIndex: "dataPagamento",
      key: "dataPagamento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Comprovante",
      key: "comprovantes",
      render: (_, registro) => (
        <Badge count={registro.comprovantes?.length ?? 0} size="small" offset={[-4, 2]}>
          <Button
            type="link"
            size="small"
            icon={<PaperClipOutlined />}
            onClick={() => setComprovantesPagamentoId(registro.id)}
          >
            Comprovantes
          </Button>
        </Badge>
      ),
    },
    {
      title: "Conferência / Troca",
      key: "conferenciaTroca",
      render: (_, registro) => {
        if (registro.trocado)
          return (
            <>
              <div>{formatData(registro.trocaData)}</div>
              <div className="text-xs opacity-70">{registro.trocaPorNome}</div>
            </>
          );
        if (!registro.conferido) return "-";
        return (
          <>
            <div>{formatData(registro.dataConferencia)}</div>
            <div className="text-xs opacity-70">{registro.conferidoPorNome}</div>
          </>
        );
      },
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, registro) =>
        !registro.trocado && !registro.conferido && canRecebimentoFinanceiro ? (
          <Popconfirm
            title="Confirmar o recebimento deste PIX/Transferência?"
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(
                () => conferirPagamentosPix([registro.id]),
                "Recebimento conferido com sucesso!"
              )
            }
          >
            <Button type="link" size="small">
              Confirmar recebimento
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Controle Pix/Transferência</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-center mb-6">
          <Input
            allowClear
            placeholder="Buscar por nome ou CPF"
            className="w-80"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            allowClear
            mode="multiple"
            placeholder="Todas as unidades"
            className="w-64"
            value={unidadeIds}
            onChange={(value) => setUnidadeIds(value)}
            options={unidades.map((u: any) => ({ value: u.id, label: u.nome }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={periodo}
            onChange={(datas) => setPeriodo(datas as [Dayjs | null, Dayjs | null] | null)}
            placeholder={["Data inicial", "Data final"]}
          />
        </div>

        <div className="mb-4 flex items-center gap-6">
          <Radio.Group
            value={filtroSituacao}
            onChange={(e) => setFiltroSituacao(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: "⏳ A Conferir", value: "aConferir" },
              { label: "✅ Conferidos", value: "conferidos" },
              { label: "Todos", value: "todos" },
            ]}
          />
          {canRecebimentoFinanceiro && (
            <Popconfirm
              title="Confirmar recebimento?"
              description={`Confirmar o recebimento de ${pendentesSelecionados.length} pagamento(s) em PIX/Transferência?`}
              okText="Sim"
              cancelText="Não"
              onConfirm={() =>
                executar(
                  () => conferirPagamentosPix(pendentesSelecionados),
                  "Recebimento conferido com sucesso!"
                )
              }
            >
              <Button type="primary" disabled={pendentesSelecionados.length === 0}>
                Confirmar recebimento dos selecionados
              </Button>
            </Popconfirm>
          )}
          <span className="text-zinc-300">
            {pagamentos.length} pagamento(s) — Total {formatValor(totalFiltrado)}
          </span>
        </div>

        <Table
          columns={colunas}
          dataSource={pagamentos}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selecionados,
            onChange: (keys: React.Key[]) => setSelecionados(keys as number[]),
            getCheckboxProps: (registro) => ({
              disabled: registro.conferido || registro.trocado,
            }),
          }}
        />

        <ComprovantesModal
          pagamento={pagamentoDosComprovantes}
          onClose={() => setComprovantesPagamentoId(null)}
          onChanged={carregarPagamentos}
        />

      </ConfigProvider>
    </>
  );
}
